/**
 * "Got photos from {event}?" banner — the mode-2 (prompt-after-event) flow.
 * Shown globally (mounted in Layout) for any recent GroupEvent the user attended in a group
 * where they've opted into 'prompt' or 'auto' photo posting (see GroupMember.photoPostingMode).
 * Candidate photos are matched against the event's time window via EXIF DateTimeOriginal,
 * client-side only — nothing is uploaded until the user confirms.
 */
import React, { useEffect, useRef, useState } from 'react';
import { parse as parseExif } from 'exifr';
import { groupsApi, API_BASE_URL } from '../services/api';
import { uploadBlobThroughStorage, showToast } from 'shared-ui';
import { syncAutoPhotoWatching } from '../utils/autoPhotoUpload';

interface RecentEvent {
  attendeeId: string;
  eventId: string;
  groupId: string;
  groupName: string;
  title: string;
  date: string;
  time?: string | null;
  location?: string | null;
  chatChannelId?: string | null;
}

interface CandidatePhoto {
  file: File;
  previewUrl: string;
  inWindow: boolean;
  selected: boolean;
}

const EventPhotoPrompt: React.FC = () => {
  const [events, setEvents] = useState<RecentEvent[]>([]);
  const [activeEvent, setActiveEvent] = useState<RecentEvent | null>(null);
  const [candidates, setCandidates] = useState<CandidatePhoto[]>([]);
  const [checkingPhotos, setCheckingPhotos] = useState(false);
  const [sharing, setSharing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await groupsApi.getMyRecentEventsForPhotoPrompt();
        if (!cancelled && res?.success && Array.isArray(res?.data)) {
          setEvents(res.data);
        }
      } catch {
        // Best-effort banner — a failed check shouldn't disrupt the rest of the app.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Native-only (no-ops on web) — starts/stops the Android background camera-roll watcher to
  // match whichever groups currently have an open 'auto' event window. Re-checked whenever the
  // app comes back to the foreground, since a window opening/closing is time-driven, not just
  // event-driven from within this app session.
  useEffect(() => {
    syncAutoPhotoWatching();
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncAutoPhotoWatching();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const currentEvent = events[0];

  const dismiss = async (eventId: string) => {
    setEvents((prev) => prev.filter((e) => e.eventId !== eventId));
    setActiveEvent(null);
    setCandidates([]);
    try {
      await groupsApi.dismissEventPhotoPrompt(eventId);
    } catch {
      // best-effort
    }
  };

  /**
   * "Remind me later" — the override field is minutes-after-event-start, not minutes-from-now,
   * so translate: override = (elapsed since start) + snoozeMinutes, which makes
   * eventStart + override land exactly `snoozeMinutes` from now.
   */
  const snooze = async (ev: RecentEvent, snoozeMinutes: number) => {
    const start = new Date(ev.date);
    const timeMatch = ev.time ? /^(\d{1,2}):(\d{2})/.exec(ev.time) : null;
    if (timeMatch) start.setHours(parseInt(timeMatch[1], 10), parseInt(timeMatch[2], 10), 0, 0);
    const elapsedMinutes = Math.max(0, Math.round((Date.now() - start.getTime()) / 60_000));

    setEvents((prev) => prev.filter((e) => e.eventId !== ev.eventId));
    try {
      await groupsApi.setEventPhotoPromptDelay(ev.eventId, elapsedMinutes + snoozeMinutes);
    } catch {
      // best-effort
    }
  };

  const openPicker = (ev: RecentEvent) => {
    setActiveEvent(ev);
    fileInputRef.current?.click();
  };

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !activeEvent) return;
    setCheckingPhotos(true);
    try {
      const windowRes = await groupsApi.getEventPhotoWindow(activeEvent.groupId, activeEvent.eventId);
      const startsAt = windowRes?.success ? new Date(windowRes.data.startsAt) : null;
      const endsAt = windowRes?.success ? new Date(windowRes.data.endsAt) : null;

      const built: CandidatePhoto[] = await Promise.all(
        Array.from(fileList).map(async (file) => {
          let takenAt: Date | null = null;
          try {
            const tags = await parseExif(file, ['DateTimeOriginal']);
            if (tags?.DateTimeOriginal) takenAt = new Date(tags.DateTimeOriginal);
          } catch {
            // No EXIF (e.g. screenshots) or unreadable — still selectable, just not pre-checked.
          }
          const inWindow = !!(takenAt && startsAt && endsAt && takenAt >= startsAt && takenAt <= endsAt);
          return {
            file,
            previewUrl: URL.createObjectURL(file),
            inWindow,
            selected: inWindow,
          };
        })
      );
      setCandidates(built);
    } finally {
      setCheckingPhotos(false);
    }
  };

  const toggleSelected = (idx: number) => {
    setCandidates((prev) => prev.map((c, i) => (i === idx ? { ...c, selected: !c.selected } : c)));
  };

  const shareSelected = async () => {
    if (!activeEvent?.chatChannelId) {
      showToast('This event has no group chat to share into.', 'error');
      return;
    }
    const chosen = candidates.filter((c) => c.selected);
    if (chosen.length === 0) return;
    setSharing(true);
    try {
      const uploaded = await Promise.all(
        chosen.map((c) =>
          uploadBlobThroughStorage({
            apiBaseUrl: API_BASE_URL,
            tokenKey: 'mangoPersonalToken',
            file: c.file,
            meta: { fileType: 'ATTACHMENT' },
          })
        )
      );
      const attachments = uploaded
        .filter((f) => !!f.url)
        .map((f) => ({ type: 'image' as const, url: f.url as string, name: f.filename }));
      if (attachments.length === 0) {
        showToast('Upload succeeded but no file URL was returned.', 'error');
        return;
      }
      const res = await groupsApi.sendGroupMessage(activeEvent.groupId, '', {
        channelId: activeEvent.chatChannelId,
        type: 'image',
        attachments,
      });
      if (!res?.success) {
        showToast(res?.error || 'Failed to share photos', 'error');
        return;
      }
      await dismiss(activeEvent.eventId);
    } catch (err) {
      console.error('Share event photos failed:', err);
      showToast('Failed to upload photos', 'error');
    } finally {
      setSharing(false);
    }
  };

  if (!currentEvent) return null;

  const selectedCount = candidates.filter((c) => c.selected).length;

  return (
    <>
      <div
        style={{
          background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
          borderRadius: '12px',
          padding: '14px 18px',
          marginBottom: '20px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong style={{ fontSize: '14px' }}>📸 Got photos from {currentEvent.title}?</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>Share them to {currentEvent.groupName}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => openPicker(currentEvent)}
            style={{ padding: '8px 16px', background: 'white', color: '#15803d', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
          >
            Choose photos
          </button>
          <button
            type="button"
            onClick={() => snooze(currentEvent, 60)}
            title="Ask again in an hour"
            style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
          >
            ⏰ Remind me later
          </button>
          <button
            type="button"
            onClick={() => dismiss(currentEvent.eventId)}
            style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
          >
            Not now
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {activeEvent && candidates.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '640px', width: '100%', maxHeight: '85vh', overflow: 'auto' }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>Share photos from {activeEvent.title}</h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
              Photos taken during the event are pre-selected. Tap any photo to change your selection.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              {candidates.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleSelected(i)}
                  style={{
                    position: 'relative',
                    padding: 0,
                    border: c.selected ? '3px solid #16a34a' : '3px solid transparent',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    background: 'none',
                  }}
                >
                  <img src={c.previewUrl} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block', opacity: c.selected ? 1 : 0.5 }} />
                  {!c.inWindow && (
                    <span style={{ position: 'absolute', bottom: 2, left: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '9px', padding: '2px 4px', borderRadius: '4px' }}>
                      outside event time
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setCandidates([]);
                  setActiveEvent(null);
                }}
                style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={shareSelected}
                disabled={sharing || selectedCount === 0}
                style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: sharing ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                {sharing ? 'Sharing…' : `Share ${selectedCount} photo${selectedCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {checkingPhotos && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, color: 'white' }}>
          Checking photo timestamps…
        </div>
      )}
    </>
  );
};

export default EventPhotoPrompt;
